import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { PageHero } from '@/components/site';
import { AudienceMain } from './audienceParts';
import { LEGAL_LINK, LegalBody } from './legalParts';

/**
 * Terms of Service (`/terms`) under SLOT_MODEL. Keeps today's sections (acceptance,
 * offer redemption, conduct, disclaimer, liability, changes, contact) and replaces
 * "Subscriptions and Billing" with the slot model's terms (ADR-0011): slots billed per
 * venue, quarterly or annual terms, 3-month minimum, category exclusivity, the Venue
 * Sponsor 12-month commitment, changes at the next print run, card payment, and the
 * free listing as the only free product. Today's page names no jurisdiction; neither
 * does this one.
 *
 * Sept 2026 redesign: the package's reading layout (site/terms/index.html: a table of
 * contents beside the text, legalParts.jsx) with the wording unchanged, word for word
 * (legal.redesign.test.jsx compares it with the wording of September 25, 2026). The
 * wording changes only with the owner's sign-off.
 */

export const TERMS_LAST_UPDATED = { label: 'September 25, 2026', iso: '2026-09-25' };

export const TERMS_SEO = {
  title: 'Terms of Service',
  description:
    "Tappd's terms: local offers, the free listing, advertising slots billed per venue, category exclusivity, Venue Sponsor terms and the print schedule.",
  url: '/terms',
};

const MAIL = (
  <a href="mailto:hello@tappd.app" className={LEGAL_LINK}>
    hello@tappd.app
  </a>
);

export const TERMS_SECTIONS = [
  {
    id: 'acceptance',
    title: 'Acceptance of Terms',
    body: [
      "By accessing and using Tappd's services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.",
    ],
  },
  {
    id: 'offer-redemption',
    title: 'Offer Redemption',
    body: [
      'Offers are subject to availability and business discretion. You claim an offer with your email address and receive a code. Each code can typically be used once per customer unless otherwise stated. Offers cannot be combined with other promotions unless explicitly permitted.',
    ],
  },
  {
    id: 'free-listing',
    title: 'The Free Listing',
    body: [
      'Any local business can create a free listing with one offer. It appears on the local deals page and costs nothing. The free listing is the only free product: it does not include coasters, and paid slots have no free period.',
    ],
  },
  {
    id: 'slots-and-billing',
    title: 'Advertising Slots and Billing',
    body: [
      <>
        Paid advertising is sold as slots, per venue: Standard, Premium and Venue Sponsor. Each venue has a fixed
        number of each (normally 8 Standard, 4 Premium and 1 Venue Sponsor). Prices per venue are on our{' '}
        <Link to="/pricing" className={LEGAL_LINK}>
          Pricing page
        </Link>
        .
      </>,
    ],
    points: [
      'Each slot is billed per venue. A business with slots at several venues pays for each one.',
      'Paid slots have a 3-month minimum and are billed quarterly in advance, to match the quarterly print run. Standard and Premium can instead be prepaid for a year.',
      'You pay by card through our payment processor, Stripe. Billing starts when you buy a slot.',
      'Your slot renews automatically at the end of each term until you cancel it.',
      'You can cancel at any time. Cancellation takes effect at the end of the term you have already paid for (and never before the 3-month minimum ends); your slot stays live until then.',
      'We do not refund part of a term you have already paid for, except where the law requires us to.',
      'If we change the price of your slot, we will tell you at least 30 days before the change takes effect.',
      'If a payment fails, your offers are paused until you update your payment details. Once payment goes through, they go live again.',
    ],
  },
  {
    id: 'category-exclusivity',
    title: 'One Advertiser per Category',
    body: [
      'Each venue takes one advertiser per business category: one realtor, one gym, one dentist. Slots are first come, first served. A slot renewed on time keeps its category at that venue. If a slot is cancelled or not renewed, the category becomes available to other businesses at that venue.',
    ],
  },
  {
    id: 'venue-sponsor',
    title: 'Venue Sponsor',
    body: [
      'The Venue Sponsor slot is sold as an annual deal: a 12-month commitment, billed quarterly. You can ask us to take the sponsorship down early, but the rest of the 12 months is still billed. Any discount is fixed at the time of purchase for the whole commitment.',
    ],
  },
  {
    id: 'print-schedule',
    title: 'Print Schedule and Changes',
    body: [
      "Coasters are printed once a quarter. A new or changed tile or coaster design goes on the next quarter's coasters. Changes to your listing and offer on the scan page go live the same day.",
    ],
  },
  {
    id: 'user-conduct',
    title: 'User Conduct',
    body: [
      'You agree to use Tappd services only for lawful purposes. Fraudulent use of claim codes or abuse of the system may result in account termination and legal action.',
    ],
  },
  {
    id: 'disclaimer',
    title: 'Disclaimer',
    body: [
      'Tappd acts as a marketing platform connecting customers with local businesses. We are not responsible for the quality of goods or services provided by participating businesses. All disputes should be resolved directly with the business.',
    ],
  },
  {
    id: 'liability',
    title: 'Limitation of Liability',
    body: [
      'Tappd shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our services.',
    ],
  },
  {
    id: 'changes',
    title: 'Changes to Terms',
    body: [
      'We reserve the right to modify these terms at any time. Continued use of our services after changes constitutes acceptance of the modified terms.',
    ],
  },
  {
    id: 'contact',
    title: 'Contact',
    body: [<>Questions about these terms? Contact us at {MAIL}.</>],
  },
];

export default function TermsSlot({ lastUpdated = TERMS_LAST_UPDATED }) {
  return (
    <AudienceMain>
      <SEO title={TERMS_SEO.title} description={TERMS_SEO.description} url={TERMS_SEO.url} />
      <PageHero
        eyebrow="Legal"
        title="Terms of Service"
        lede="The rules for using Tappd: claiming offers, the free listing, and advertising slots at our partner venues."
      />
      <LegalBody lastUpdated={lastUpdated} sections={TERMS_SECTIONS} />
    </AudienceMain>
  );
}
